import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { StatusChip } from "../../Components/Kit";
import { fetchEmailBrand, fetchEmailSetup, previewLibraryItem, saveEmailBrand } from "../../Helpers/notificationConsoleApi";

// Notification Centre > Setup > Email (R6.6): how email is sending (server settings) and the Email look: the
// logo, colours, sign-off and footer of every email written in the simple editor (moved from the Library).
const MODE = {
    off: { tone: "neutral", name: "Off", text: "No email is sent. Push, WhatsApp and SMS work as usual." },
    shadow: { tone: "wait", name: "Shadow", text: "Emails are built and written to the delivery log, but nothing is sent." },
    live: { tone: "ok", name: "Live", text: "Emails go to customers with a verified address who have not switched email off." },
};

const SAMPLE_EMAIL = { channel: "email", name: "Sample", language: "en", email: { mode: "simple", subject: "Your booking {bookingId} is confirmed", heading: "See you soon, {name}", body: "Your **{car}** is booked for {start} at {hub}.\n\nShow the handover code to the executive when the car arrives.", buttonLabel: "View booking", buttonUrl: "https://getmydrive.in/app/bookings", note: "Need help? Reply to this email or open Support in the app." } };

const BrandEditor = ({ brand: start, onSaved }) => {
    const [brand, setBrand] = useState(start);
    const [html, setHtml] = useState("");
    const [busy, setBusy] = useState(false);
    const set = (patch) => setBrand((prev) => ({ ...prev, ...patch }));
    useEffect(() => {
        const timer = setTimeout(async () => {
            const result = await previewLibraryItem({ ...SAMPLE_EMAIL, brand });
            if (result.ok) setHtml(result.data.email?.html || "");
        }, 350);
        return () => clearTimeout(timer);
    }, [brand]);
    const save = async () => {
        setBusy(true);
        const result = await saveEmailBrand({ logoUrl: brand.logoUrl || "", brandName: brand.brandName || "", primaryColor: brand.primaryColor || "", accentColor: brand.accentColor || "", signOff: brand.signOff || "", footer: brand.footer || "" });
        setBusy(false);
        if (!result.ok) return toast.error(result.message);
        toast.success("Email look saved. Every simple-editor email uses it within about 30 seconds.");
        setBrand(result.data.brand || brand);
        return onSaved(result.data.brand);
    };
    const color = (key, label) => (
        <div className="col-sm-6">
            <label className="form-label small mb-1" htmlFor={`brand-${key}`}>{label}</label>
            <div className="d-flex gap-2">
                <input type="color" className="form-control form-control-color" value={/^#[0-9a-fA-F]{6}$/.test(brand[key] || "") ? brand[key] : "#000000"} onChange={(e) => set({ [key]: e.target.value.toUpperCase() })} aria-label={label} />
                <input id={`brand-${key}`} className="form-control form-control-sm nc-mono" value={brand[key] || ""} onChange={(e) => set({ [key]: e.target.value })} />
            </div>
        </div>
    );
    return (
        <div>
            <div className="nc-editor-head">
                <div>
                                        <div className="fw-semibold">Email look</div>
                    <div className="nc-sub">Used by every email written in the simple editor. HTML emails keep their own look.</div>
                </div>
            </div>
            <div className="nc-lib-editor">
                <div className="nc-lib-form">
                    <div className="row g-2">
                        <div className="col-sm-6"><label className="form-label small mb-1" htmlFor="brand-name">Brand name</label><input id="brand-name" className="form-control form-control-sm" maxLength={60} value={brand.brandName || ""} onChange={(e) => set({ brandName: e.target.value })} /></div>
                        <div className="col-sm-6"><label className="form-label small mb-1" htmlFor="brand-logo">Logo link (https, optional; the name shows without it)</label><input id="brand-logo" className="form-control form-control-sm" value={brand.logoUrl || ""} onChange={(e) => set({ logoUrl: e.target.value })} /></div>
                        {color("primaryColor", "Main colour (heading, button)")}
                        {color("accentColor", "Accent colour (top line)")}
                        <div className="col-12"><label className="form-label small mb-1" htmlFor="brand-sign">Sign-off</label><input id="brand-sign" className="form-control form-control-sm" maxLength={120} value={brand.signOff || ""} onChange={(e) => set({ signOff: e.target.value })} /></div>
                        <div className="col-12"><label className="form-label small mb-1" htmlFor="brand-foot">Footer (address, support number ...)</label><textarea id="brand-foot" className="form-control form-control-sm" rows={3} maxLength={500} value={brand.footer || ""} onChange={(e) => set({ footer: e.target.value })} /></div>
                    </div>
                    <div className="nc-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setBrand(start)} disabled={busy}>Undo changes</button>
                    </div>
                </div>
                <div className="nc-lib-preview">
                    <div className="nc-preview-label">Sample email</div>
                    <iframe title="Email look preview" className="nc-email-frame nc-lib-email" sandbox="" srcDoc={`<!doctype html><html><body style="margin:0;background:#F4F1F6;">${html}</body></html>`} />
                </div>
            </div>
        </div>
    );
};

const EmailSetup = () => {
    const [info, setInfo] = useState(null);
    const [brand, setBrand] = useState(null);

    const load = useCallback(async () => {
        const [setup, look] = await Promise.all([fetchEmailSetup(), fetchEmailBrand()]);
        if (setup.ok) setInfo(setup.data); else toast.error(setup.message);
        if (look.ok) setBrand(look.data.brand || {}); else setBrand({});
    }, []);
    useEffect(() => { load(); }, [load]);

    const mode = MODE[info?.mode] || MODE.off;
    return (
        <>
            <div className="settings-card">
                <h5 className="fw-semibold mb-1">Email</h5>
                <p className="text-muted small mb-3">Booking and trip emails through the Gmail account the platform already uses for sign-in codes.</p>
                {!info ? <Skeleton height={90} /> : (
                    <>
                        {!info.configured ? <div className="nc-find nc-find-problem mb-3">The Gmail account is not connected on this server, so nothing can be sent.</div> : null}
                        <div className="nc-health-grid">
                            <div className="nc-stat">
                                <div className="nc-stat-label">Mode</div>
                                <div className="nc-stat-value"><StatusChip tone={mode.tone}>{mode.name}</StatusChip></div>
                                <div className="nc-sub mt-1">{mode.text}</div>
                            </div>
                            <div className="nc-stat">
                                <div className="nc-stat-label">Sent today</div>
                                <div className="nc-stat-value">{info.sentToday} <span className="nc-sub">/ {info.dailyBudget}</span></div>
                                <div className="nc-sub">{info.reserve} of these are kept for booking and trip mail; campaigns stop before them.</div>
                            </div>
                            <div className="nc-stat">
                                <div className="nc-stat-label">Links</div>
                                <div className="small">Website links: <strong className={info.siteUrl ? "text-success" : "text-muted"}>{info.siteUrl ? "set" : "not set"}</strong></div>
                                <div className="small">Unsubscribe link: <strong className={info.unsubscribe ? "text-success" : "text-muted"}>{info.unsubscribe ? "set" : "not set"}</strong></div>
                                <div className="nc-sub">Sender name: {info.fromName || "-"}</div>
                            </div>
                        </div>
                        <div className="nc-sub mt-2">The mode, the daily budget and the links are server settings (EMAIL_MODE, EMAIL_DAILY_BUDGET ...). Change them in the server environment, then restart the Admin backend.</div>
                    </>
                )}
            </div>
            <div className="settings-card mt-3">
                {brand === null ? <Skeleton height={240} /> : <BrandEditor brand={brand} onSaved={(saved) => { if (saved) setBrand(saved); }} />}
            </div>
        </>
    );
};

export default EmailSetup;
